"use client";

import { Hand } from "lucide-react";
import { hydrateProposalPayload } from "~/lib/siteglue/ai-employee-proposal";
import type {
  AiOpportunityReportPayload,
  DesiredBusinessOutcomePlan,
} from "~/lib/siteglue/consultant-onboarding";
import { AI_EMPLOYEE_PLANS } from "~/lib/siteglue/ai-employee-plans";
import { defaultAvatarUrlForAgentName } from "~/lib/siteglue/ai-employee-role-catalog";
import { cn } from "~/lib/utils";

type AiEmployeeProposalViewProps = {
  report: AiOpportunityReportPayload;
  className?: string;
};

function Section({
  title,
  children,
  id,
}: {
  title: string;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <section
      id={id}
      className="border-b border-slate-100 py-3 last:border-0"
      aria-labelledby={id ? `${id}-heading` : undefined}
    >
      <h3
        id={id ? `${id}-heading` : undefined}
        className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400"
      >
        {title}
      </h3>
      <div className="text-sm leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

function List({ items }: { items: string[] }) {
  if (!items.length) return <p className="text-slate-500">None listed yet</p>;
  return (
    <ul className="list-disc space-y-0.5 pl-4">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function OutcomeCard({ plan }: { plan: DesiredBusinessOutcomePlan }) {
  return (
    <article
      className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4"
      data-testid="outcome-plan-card"
      aria-label={`Outcome: ${plan.desiredBusinessOutcome}`}
    >
      <h4 className="text-sm font-semibold text-slate-900">{plan.desiredBusinessOutcome}</h4>
      <p className="mt-2 text-sm text-slate-600">{plan.howEmployeeHelpsAchieveIt}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Recommended Actions
          </p>
          <List items={plan.recommendedActions} />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Communication Channels
          </p>
          <List items={plan.recommendedChannels} />
        </div>
      </div>
      <div className="mt-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Human Team Collaboration
        </p>
        <List items={plan.humanTeamCollaboration} />
      </div>
      <p className="mt-3 text-xs text-slate-500">
        <span className="font-medium text-slate-600">Expected business impact: </span>
        {plan.expectedBusinessImpact}
      </p>
    </article>
  );
}

/** Ensure legacy session payloads still render as a proposal. */
function hydrateLegacyStrategy(report: AiOpportunityReportPayload): AiOpportunityReportPayload {
  const company =
    report.companyName?.trim() ||
    report.businessSnapshot.find((l) => /^Organization:/i.test(l))?.replace(/^Organization:\s*/i, "") ||
    "your organization";
  const name = report.recommendedEmployee?.name || "Samantha";
  const role =
    report.recommendedEmployee?.role ||
    report.recommendedAppointmentRoles[0] ||
    "Live Chat Support Agent";
  const planName =
    AI_EMPLOYEE_PLANS.find((p) => p.id === report.recommendedPlan)?.name ?? "Intern";

  const base: AiOpportunityReportPayload = {
    ...report,
    companyName: company,
    priorities: report.priorities?.length ? report.priorities : report.businessGoals,
    whatILearned: report.whatILearned?.length ? report.whatILearned : report.businessSnapshot,
    recommendedEmployee: report.recommendedEmployee ?? {
      name,
      role,
      careerLevel: planName,
      summary: report.executiveSummary.slice(0, 180),
      profileImageUrl: defaultAvatarUrlForAgentName(name),
    },
    recommendationRationale: report.recommendationRationale?.length
      ? report.recommendationRationale
      : report.planReasons,
    howEmployeeHelps: report.howEmployeeHelps?.length
      ? report.howEmployeeHelps
      : report.implementationPriorities.slice(0, 5),
    humanTeamCollaboration: report.humanTeamCollaboration?.length
      ? report.humanTeamCollaboration
      : report.humanInTheLoopStrategy,
    firstImplementation: report.firstImplementation ?? {
      startWith: [
        "Website Chat",
        "Core FAQs",
        "Inquiry capture",
        "Department routing",
        "Email conversation reports",
        "Testing with the Human Team",
      ],
      considerAfter:
        report.recommendedPlan === "intern"
          ? ["Phone", "SMS", "Email channel", "Direct scheduling", "Additional Roles"]
          : ["Direct scheduling", "Additional Roles"],
    },
    careerLevelWhy: report.careerLevelWhy?.length ? report.careerLevelWhy : report.planReasons,
    careerLevelIncludes: report.careerLevelIncludes?.length
      ? report.careerLevelIncludes
      : ["Website Chat", "Team Handoff™"],
    promotionTriggers: report.promotionTriggers?.length
      ? report.promotionTriggers
      : ["Confirmed paid-channel or integration needs after testing"],
    expectedBenefits: report.expectedBenefits?.length
      ? report.expectedBenefits
      : report.estimatedImpact.map((i) => i.category),
  };

  return hydrateProposalPayload(base);
}

export function AiEmployeeProposalView({ report, className }: AiEmployeeProposalViewProps) {
  const s = hydrateLegacyStrategy(report);
  const emp = s.recommendedEmployee!;
  const avatar = emp.profileImageUrl || defaultAvatarUrlForAgentName(emp.name);
  const title = s.proposalTitle || s.strategyTitle || `SiteGlue Agent Proposal for ${s.companyName}`;

  return (
    <article
      className={cn(
        "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5",
        className,
      )}
      data-testid="ai-employee-proposal"
      aria-label={title}
    >
      <div className="sr-only" role="status" aria-live="polite">
        {title} is ready
      </div>

      <header className="mb-3 border-b border-slate-100 pb-3">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
          AI Employee Proposal
        </p>
        <h2 className="mt-1 text-lg font-semibold text-slate-900">{title}</h2>
        {s.subheading ? (
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.subheading}</p>
        ) : null}
        <dl className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
          <div>
            <dt className="font-semibold uppercase tracking-wide text-slate-400">Prepared For</dt>
            <dd>{s.preparedFor || s.companyName}</dd>
          </div>
          <div>
            <dt className="font-semibold uppercase tracking-wide text-slate-400">Prepared By</dt>
            <dd>{s.preparedBy || "Trish, SiteGlue AI Solutions Consultant"}</dd>
          </div>
        </dl>
      </header>

      <Section title="Executive Summary" id="executive-summary">
        <p>{s.executiveSummary}</p>
      </Section>

      <Section title="What I Learned" id="what-i-learned">
        <List items={s.whatILearned ?? []} />
      </Section>

      <Section title="Your Desired Business Outcomes" id="desired-outcomes">
        <div className="space-y-3">
          {(s.desiredOutcomePlans ?? []).map((plan) => (
            <OutcomeCard key={plan.desiredBusinessOutcome} plan={plan} />
          ))}
        </div>
      </Section>

      <Section title="Recommended AI Employee" id="recommended-employee">
        <div className="flex items-start gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={avatar}
            alt={emp.name}
            className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-2 ring-white shadow-sm"
          />
          <div className="min-w-0">
            <p className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#6b8f2e]">
              <Hand className="h-3 w-3" aria-hidden />
              Human-in-the-Loop™
            </p>
            <p className="text-base font-semibold text-slate-900">{emp.name}</p>
            <p className="text-sm font-medium text-slate-800">{emp.role}</p>
            <p className="text-xs text-slate-500">Recommended Career Level: {emp.careerLevel}</p>
            <p className="mt-1 text-sm text-slate-600">{emp.summary}</p>
          </div>
        </div>
      </Section>

      <Section title="Why This AI Employee" id="why-this-employee">
        <List items={s.recommendationRationale ?? []} />
      </Section>

      <Section title="Recommended First Implementation" id="first-implementation">
        <p className="mb-1 font-medium text-slate-800">Start with:</p>
        <List items={s.firstImplementation?.startWith ?? []} />
        {s.firstImplementation?.considerAfter?.length ? (
          <>
            <p className="mb-1 mt-3 font-medium text-slate-800">Future capabilities (after testing):</p>
            <List items={s.firstImplementation.considerAfter} />
          </>
        ) : null}
      </Section>

      <Section title="Human Team Collaboration" id="human-team">
        <List items={s.humanTeamCollaboration ?? []} />
      </Section>

      <Section title="Recommended Career Level" id="career-level">
        <p className="font-semibold text-slate-900">{emp.careerLevel}</p>
        <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-400">Why this level</p>
        <List items={s.careerLevelWhy ?? []} />
        <p className="mt-2 text-xs font-medium uppercase tracking-wide text-slate-400">Included now</p>
        <List items={s.careerLevelIncludes ?? []} />
      </Section>

      <Section title="Future Career Path" id="future-career-path">
        <List items={s.futureCareerPath ?? s.promotionTriggers ?? []} />
      </Section>

      <Section title="Next Step" id="next-step">
        <p>{s.nextStep || s.nextSteps?.[0] || "Review the proposal and continue onboarding."}</p>
        {(s.nextSteps?.length ?? 0) > 1 ? (
          <ol className="mt-2 list-decimal space-y-0.5 pl-4">
            {(s.nextSteps ?? []).slice(1).map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        ) : null}
      </Section>

      {s.customMcpStatus ? (
        <Section title="Custom Integration Path">
          <p className="text-sm">Status: {s.customMcpStatus}</p>
        </Section>
      ) : null}

      <p className="mt-3 text-xs text-slate-400">
        Always a Human-in-the-Loop™ · Team Handoff™ · Team Takeover™
      </p>
    </article>
  );
}

/** @deprecated Use AiEmployeeProposalView */
export const AiImplementationStrategyView = AiEmployeeProposalView;

/** @deprecated Use AiEmployeeProposalView */
export const AiOpportunityReportView = AiEmployeeProposalView;
